import React, { useEffect, useState, useMemo } from "react";
import { InputField } from "@cakrawalajakarta/shared-components";
// import { Country, State, City } from "country-state-city"; // Dependency removed to save size

export const InputFieldProfile = ({
  formData,
  handleChange,
  isFormDisabled = false,
  labelColor,
}) => {
  const [countries, setCountries] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [countryMap, setCountryMap] = useState({});
  const [stateMap, setStateMap] = useState({});
  const [rawCountries, setRawCountries] = useState([]);
  const [provinceIds, setProvinceIds] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState({
    countries: false,
    provinces: false,
    cities: false,
  });

  const GEO_API_BASE = `${import.meta.env.VITE_API_BASE_URL}/api/geo`;

  const cache = useMemo(() => new Map(), []);

  const fetchWithCache = async (url, cacheKey, type) => {
    if (cache.has(cacheKey)) {
      return cache.get(cacheKey);
    }
    setLoading((prev) => ({ ...prev, [type]: true }));
    try {
      const res = await fetch(url);
      const data = await res.json();
      if (data?.geonames) {
        cache.set(cacheKey, data);
        return data;
      } else {
        console.error(`No geonames data in response for ${cacheKey}:`, data);
        setErrors((prev) => ({
          ...prev,
          [type]: "Failed to load location data. Please try again later.",
        }));
        return null;
      }
    } catch (err) {
      console.error(`Error fetching ${cacheKey}:`, err);
      setErrors((prev) => ({
        ...prev,
        [type]: "Failed to load location data. Please try again later.",
      }));
      return null;
    } finally {
      setLoading((prev) => ({ ...prev, [type]: false }));
    }
  };

  useEffect(() => {
    // Only use GeoNames API
    fetchWithCache(
      `${GEO_API_BASE}/countries`,
      "countries",
      "countries"
    ).then((data) => {
      if (data?.geonames) {
        const countryList = data.geonames
          .map((country) => ({
            value: country.countryName,
            label: country.countryName,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
        setCountries(countryList);
        setRawCountries(data.geonames);
      } else {
        console.warn("Using empty country list as fallback");
        setCountries([]);
        setRawCountries([]);
      }
    });

  }, [cache]);

  useEffect(() => {
    if (formData.negara) {
      setProvinces([]);
      setCities([]);
      
      // Use GeoNames API
      if (rawCountries.length > 0) {
        const selectedCountry = rawCountries.find(
          (c) => c.countryName === formData.negara
        );
        if (!selectedCountry) {
          console.warn(`Country not found: ${formData.negara}`);
          return;
        }
  
        fetchWithCache(
          `${GEO_API_BASE}/children/${selectedCountry.geonameId}?lang=en`,
          `provinces_${selectedCountry.geonameId}`,
          "provinces"
        ).then((data) => {
          if (data?.geonames) {
            const provinceList = data.geonames
              .map((prov) => ({
                value: prov.name,
                label: prov.name,
              }))
              .sort((a, b) => a.label.localeCompare(b.label));
            const idMap = {};
            data.geonames.forEach((prov) => {
              idMap[prov.name] = prov.geonameId;
            });
            setProvinces(provinceList);
            setProvinceIds(idMap);
          }
        });
      }
    }
  }, [formData.negara, rawCountries]);

  useEffect(() => {
    if (formData.provinsi && formData.negara) {
      setCities([]);

      // Use GeoNames API
      if (provinceIds[formData.provinsi]) {
        const provinceId = provinceIds[formData.provinsi];
  
        fetchWithCache(
          `${GEO_API_BASE}/children/${provinceId}?lang=en`,
          `cities_${provinceId}`,
          "cities"
        ).then((data) => {
          if (data?.geonames) {
            const cityList = data.geonames.map((city) => ({
              value: city.name,
              label: city.name,
              // For cities, geonames often returns 'name' as just the city name.
            })).sort((a, b) => a.label.localeCompare(b.label));
            setCities(cityList);
          }
        });
      }
    }
  }, [formData.provinsi, formData.negara, provinceIds]);

  const validateField = (name, value) => {
    if (
      !value &&
      [
        "namaDepan",
        "namaBelakang",
        "telepon",
        "tanggalLahir",
        "gender",
        "negara",
        "provinsi",
        "kota",
        "alamat",

      ].includes(name)
    ) {
      const displayNames = {
        namaDepan: "First Name",
        namaBelakang: "Last Name",
        telepon: "Phone Number",
        tanggalLahir: "Date of Birth",
        gender: "Gender",
        negara: "Country",
        provinsi: "Province",
        kota: "City",
        alamat: "Address",
      };
      return `${displayNames[name] || name} is required`;
    }
    return "";
  };

  const handleFieldChange = (e) => {
    const { name, value } = e.target;
    let updatedFormData = { ...formData, [name]: value };

    if (name === "negara") {
      updatedFormData.provinsi = "";
      updatedFormData.kota = "";
    } else if (name === "provinsi") {
      updatedFormData.kota = "";
    }

    handleChange({ target: { name, value } });

    setErrors((prev) => ({
      ...prev,
      [name]: validateField(name, value),
    }));
  };



  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 my-4">
      {errors.countries && (
        <p className="text-red-500 col-span-full">{errors.countries}</p>
      )}
      <InputField
        label="First Name"
        name="namaDepan"
        required
        value={formData.namaDepan || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        error={errors.namaDepan}
        labelColor={labelColor}
      />
      <InputField
        label="Last Name"
        name="namaBelakang"
        required
        value={formData.namaBelakang || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        error={errors.namaBelakang}
        labelColor={labelColor}
      />
      <InputField
        label="Email"
        name="email"
        type="email"
        required
        value={formData.email}
        onChange={handleChange}
        disabled={true}
        labelColor={labelColor}
      />
      <InputField
        label="Phone Number/WhatsApp"
        name="telepon"
        required
        value={formData.telepon || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        error={errors.telepon}
        labelColor={labelColor}
      />
      <InputField
        label="Date of Birth"
        name="tanggalLahir"
        type="date"
        required
        value={formData.tanggalLahir || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        error={errors.tanggalLahir}
        labelColor={labelColor}
      />
      <InputField
        label="Gender"
        name="gender"
        type="select"
        required
        value={formData.gender || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        options={[
          { value: "", label: "Select Gender" },
          { value: "Male", label: "Male" },
          { value: "Female", label: "Female" },
        ]}
        error={errors.gender}
        labelColor={labelColor}
      />
      <InputField
        label="Country"
        name="negara"
        type="select"
        required
        value={formData.negara || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled || loading.countries}
        options={[{ value: "", label: "Select Country" }, ...countries]}
        error={errors.negara}
        aria-busy={loading.countries}
        labelColor={labelColor}
      />
      <InputField
        label="Province"
        name="provinsi"
        type="select"
        required
        value={formData.provinsi || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled || !formData.negara || loading.provinces}
        options={[{ value: "", label: "Select Province" }, ...provinces]}
        error={errors.provinsi}
        aria-busy={loading.provinces}
        labelColor={labelColor}
      />
      <InputField
        label="City"
        name="kota"
        type="select"
        required
        value={formData.kota || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled || !formData.provinsi || loading.cities}
        options={[{ value: "", label: "Select City" }, ...cities]}
        error={errors.kota}
        aria-busy={loading.cities}
        labelColor={labelColor}
      />
      <InputField
        label="Address (As per ID)"
        name="alamat"
        required
        value={formData.alamat || ""}
        onChange={handleFieldChange}
        disabled={isFormDisabled}
        error={errors.alamat}
        labelColor={labelColor}
      />

    </div>
  );
};
